import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Loader2, CheckCircle } from "lucide-react";
import { useNewsletterSubscribe } from "@/hooks/useNewsletterSubscribe";
import NewsletterHoneypot from "@/components/NewsletterHoneypot";
import { AnimatePresence, motion } from "framer-motion";
import { OutspokenWordmark } from "@/components/OutspokenLogo";
import { useCMSBlock } from "@/hooks/useCMSBlock";
import { DEFAULT_HOME_FOOTER, DEFAULT_HOME_NEWSLETTER } from "@/lib/cms-defaults";

function FooterNewsletter() {
  const { email, setEmail, honeypot, setHoneypot, loading, done, handleSubmit } = useNewsletterSubscribe();

  return (
    <div className="max-w-md">
      <p className="text-sm font-semibold text-foreground mb-1">One clear idea, every week</p>
      <p className="text-xs text-muted-foreground mb-3">A short note that helps you think straighter about the week ahead. No fluff. No spam.</p>
      {done ? (
        <div className="flex items-center gap-2 text-accent text-xs"><CheckCircle size={14} /> Subscribed!</div>
      ) : (
        <form onSubmit={handleSubmit} className="flex flex-col sm:flex-row gap-2">
          <NewsletterHoneypot value={honeypot} onChange={setHoneypot} />
          <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="your@email.com"
            className="flex-1 bg-secondary border border-border rounded-lg px-3 py-2 text-foreground placeholder:text-muted-foreground text-xs focus:outline-none focus:ring-1 focus:ring-accent/50 min-h-[44px]" />
          <button type="submit" disabled={loading}
            className="bg-accent text-accent-foreground px-4 py-2 rounded-lg text-xs font-semibold hover:bg-accent/90 disabled:opacity-50 min-h-[44px] flex items-center justify-center gap-1">
            {loading ? <Loader2 size={12} className="animate-spin" /> : "Subscribe"}
          </button>
        </form>
      )}
    </div>
  );
}

function CookieConsent() {
  const [show, setShow] = useState(false);

  useEffect(() => {
    const accepted = localStorage.getItem("outspoken_cookies_accepted");
    if (!accepted) setShow(true);
  }, []);

  const accept = () => {
    localStorage.setItem("outspoken_cookies_accepted", "true");
    setShow(false);
  };

  return (
    <AnimatePresence>
      {show && (
        <motion.div
          initial={{ y: 100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 100, opacity: 0 }}
          transition={{ duration: 0.3 }}
          className="fixed bottom-0 left-0 right-0 z-[60] bg-card border-t border-border px-4 py-3 sm:px-6"
        >
          <div className="max-w-5xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-3">
            <p className="text-xs text-muted-foreground text-center sm:text-left">
              We use essential <Link to="/cookie-policy">cookies</Link> to keep things running smoothly. No tracking, no ads.
            </p>
            <button onClick={accept}
              className="bg-accent text-accent-foreground px-5 py-2 rounded-lg text-xs font-semibold hover:bg-accent/90 transition-colors shrink-0 min-h-[36px]">
              Got it
            </button>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

export { CookieConsent };

export default function Footer() {
  const isHome = useLocation().pathname === "/";
  const { data: block } = useCMSBlock("home_footer");
  const cms = block?.content || DEFAULT_HOME_FOOTER;
  const links = cms.links || DEFAULT_HOME_FOOTER.links;
  const tagline = cms.tagline || DEFAULT_HOME_FOOTER.tagline;
  const copyright = cms.copyright_text || DEFAULT_HOME_FOOTER.copyright_text;

  // Split links into two columns
  const mid = Math.ceil(links.length / 2);
  const col1 = links.slice(0, mid);
  const col2 = links.slice(mid);

  return (
    <>
      <footer className="border-t border-border bg-background pb-20 md:pb-0">
        <div className="container max-w-5xl px-4 md:px-6 py-8 sm:py-10">
          <div className="flex flex-col sm:flex-row gap-8 sm:gap-12 mb-6 sm:mb-8">
            {!isHome && <FooterNewsletter />}
            <div className="flex-1 flex flex-col sm:flex-row sm:justify-end gap-4 text-xs text-muted-foreground">
              <div className="flex flex-col gap-2">
                {col1.map((link: any) => (
                  <Link key={link.url} to={link.url} className="hover:text-foreground transition-colors">{link.label}</Link>
                ))}
              </div>
              <div className="flex flex-col gap-2">
                {col2.map((link: any) => (
                  <Link key={link.url} to={link.url} className="hover:text-foreground transition-colors">{link.label}</Link>
                ))}
              </div>
            </div>
          </div>

          <div className="border-t border-border/50 pt-4 sm:pt-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
            <div className="space-y-2">
              <Link to="/" state={{ fromNav: true }} className="text-foreground inline-block">
                <OutspokenWordmark height={22} className="text-foreground" />
              </Link>
              <p className="text-[10px] text-muted-foreground/60 leading-relaxed">
                {tagline}
              </p>
              <Link to="/philosophy" className="text-[10px] text-muted-foreground/40 hover:text-muted-foreground transition-colors">
                Where our voices come from
              </Link>
            </div>
            <p className="text-xs text-muted-foreground">{copyright}</p>
          </div>
        </div>
      </footer>
      <CookieConsent />
    </>
  );
}
